/**
 * Task List Component
 * Displays tasks for a member with Active/Archived toggle
 * Matches AutoMatchReferrals styling
 */

import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, RefreshCw, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { tasksAPI, Task, TaskSummary, TaskStatus, TaskPriority, STATUS_LABELS } from '@/services/api/tasks';
import TaskCard from './TaskCard';
import TaskForm from './TaskForm';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface TaskListProps {
  memberId: number;
  memberName?: string;
}

export default function TaskList({ memberId, memberName }: TaskListProps) {
  const { t } = useAppTranslation();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [summary, setSummary] = useState<TaskSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [isArchived, setIsArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | ''>('');

  // UI state
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  useEffect(() => {
    loadTasks();
    loadSummary();
  }, [memberId, isArchived, statusFilter, searchQuery]);

  const loadTasks = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await tasksAPI.getTasks({
        member_id: memberId,
        is_archived: isArchived,
        status_filter: statusFilter || undefined,
        search: searchQuery || undefined,
        page: 1,
        page_size: 100,
      });

      setTasks(response.tasks);
    } catch (err) {
      console.error('Error loading tasks:', err);
      setError('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const data = await tasksAPI.getMemberTaskSummary(memberId);
      setSummary(data);
    } catch (err) {
      console.error('Error loading task summary:', err);
    }
  };

  const handleCreateTask = () => {
    setEditingTask(null);
    setShowTaskForm(true);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setShowTaskForm(true);
  };

  const handleTaskSaved = async () => {
    setShowTaskForm(false);
    setEditingTask(null);
    await loadTasks();
    await loadSummary();
  };

  const handleTaskDeleted = async () => {
    await loadTasks();
    await loadSummary();
  };

  if (loading && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error && tasks.length === 0) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-4 md:p-6">
        <p className="text-red-600 dark:text-red-400 text-sm md:text-base">Error: {error}</p>
        <button
          onClick={loadTasks}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 active:scale-95 transition-all min-h-[44px]"
        >
          {t('tasks.retry', 'Retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with summary stats */}
      {summary && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6">
          <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3">
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-2.5 md:p-3 text-center">
              <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400 mx-auto mb-1" />
              <p className="text-lg md:text-xl font-bold text-blue-600 dark:text-blue-400">{summary.total_tasks}</p>
              <span className="text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-400">{t('tasks.total', 'Total')}</span>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2.5 md:p-3 text-center">
              <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400 mx-auto mb-1" />
              <p className="text-lg md:text-xl font-bold text-gray-600 dark:text-gray-400">{summary.not_started}</p>
              <span className="text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-400">{t('tasks.not_started', 'Not Started')}</span>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-2.5 md:p-3 text-center">
              <RefreshCw className="w-4 h-4 text-blue-600 dark:text-blue-400 mx-auto mb-1" />
              <p className="text-lg md:text-xl font-bold text-blue-600 dark:text-blue-400">{summary.in_progress}</p>
              <span className="text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-400">{t('tasks.in_progress', 'In Progress')}</span>
            </div>
            <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-2.5 md:p-3 text-center">
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mx-auto mb-1" />
              <p className="text-lg md:text-xl font-bold text-green-600 dark:text-green-400">{summary.completed}</p>
              <span className="text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-400">{t('tasks.completed', 'Completed')}</span>
            </div>
            <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-2.5 md:p-3 text-center">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mx-auto mb-1" />
              <p className="text-lg md:text-xl font-bold text-red-600 dark:text-red-400">{summary.overdue}</p>
              <span className="text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-400">{t('tasks.overdue', 'Overdue')}</span>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/30 rounded-lg p-2.5 md:p-3 text-center">
              <AlertCircle className="w-4 h-4 text-orange-600 dark:text-orange-400 mx-auto mb-1" />
              <p className="text-lg md:text-xl font-bold text-orange-600 dark:text-orange-400">{summary.high_priority}</p>
              <span className="text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-400">{t('tasks.high_priority', 'High Priority')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-white">{t('tasks.task_lists', 'Task Lists')}</h2>
            {memberName && (
              <span className="text-sm text-gray-500 dark:text-gray-400">for {memberName}</span>
            )}
          </div>

          <button
            onClick={handleCreateTask}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 active:scale-95 transition-all min-h-[44px] self-start md:self-auto"
          >
            <Plus className="w-4 h-4 md:w-5 md:h-5" />
            <span className="text-sm md:text-base">{t('tasks.add_task', 'Add Task')}</span>
          </button>
        </div>

        {/* Active/Archived Toggle */}
        <div className="flex items-center space-x-2 mb-4">
          <button
            onClick={() => setIsArchived(false)}
            className={`px-4 py-2 rounded-lg transition-all min-h-[44px] ${
              !isArchived
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {t('tasks.active', 'Active')}
          </button>
          <button
            onClick={() => setIsArchived(true)}
            className={`px-4 py-2 rounded-lg transition-all min-h-[44px] ${
              isArchived
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {t('tasks.archived', 'Archived')}
          </button>
        </div>

        {/* Search and Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('tasks.enter_a_keyword', 'Enter a keyword')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent min-h-[44px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as TaskStatus | '')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent appearance-none min-h-[44px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">{t('tasks.all_statuses', 'All Statuses')}</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Refresh Button */}
          <button
            onClick={() => { loadTasks(); loadSummary(); }}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 active:scale-95 transition-all min-h-[44px]"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="text-sm md:text-base">{t('tasks.refresh', 'Refresh')}</span>
          </button>
        </div>
      </div>

      {/* Task Cards */}
      <div className="space-y-4">
        {tasks.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              {isArchived ? 'No archived tasks' : 'No active tasks'}
            </p>
            {!isArchived && (
              <button
                onClick={handleCreateTask}
                className="mt-4 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 active:scale-95 transition-all min-h-[44px]"
              >
                {t('tasks.create_your_first_task', 'Create Your First Task')}
              </button>
            )}
          </div>
        ) : (
          tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onEdit={handleEditTask}
              onDeleted={handleTaskDeleted}
              onRefresh={loadTasks}
            />
          ))
        )}
      </div>

      {/* Task Form Modal */}
      {showTaskForm && (
        <TaskForm
          memberId={memberId}
          task={editingTask}
          onClose={() => {
            setShowTaskForm(false);
            setEditingTask(null);
          }}
          onSaved={handleTaskSaved}
        />
      )}
    </div>
  );
}
